import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { AlertService } from '../../../../../services/alert.service';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TemplatesService } from '../../../../../services/templates.service';
import { PROFILES } from '../../../../../constants';
import { useSession } from '../../../../../hooks/use-session.hook';
import Select from 'react-select';
import InvalidFeedback from '../../../../../components/forms/invalid-feedback.component';
import Icon from '../../../../../components/forms/icon.component';
import Title from '../../../../../components/forms/titles.component';
import EventsSection, { getChannelEventsConfig } from './events.component';
import { useLocation } from 'react-router-dom';

function ChannelEmail({ onValidationChange }: { onValidationChange?: (isValid: boolean) => void }) {
  const { t } = useTranslation();
  const { session } = useSession();
  const location = useLocation();
  const showEvents = location.pathname.includes('sendCampaigns/flows-campaigns');
  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  const [accountsEmail, setAccountsEmail] = useState<{ value: string; label: string }[]>([]);
  const [templatesEmail, setTemplatesEmail] = useState<any[]>([]);
  const [previewEmail, setPreviewEmail] = useState<any>();
  const [variablesBySection, setVariablesBySection] = useState<{ [key: string]: any[] }>({});
  const [emptyAccountEmail, setEmptyAccountEmail] = useState(false);
  const [emptyTemplatesEmail, setEmptyTemplatesEmail] = useState(false);
  const [eventsValid, setEventsValid] = useState(true);

  const { control, watch, setValue, register } = useFormContext();

  const formValues = useMemo(() => {
    const values = watch();
    return {
      headersSelects: values.headersSelects,
      account: values.account,
      channel: values.channel,
      accountsEmail: values.accountsEmail,
      typeCampaign: values.typeCampaign,
      templatesEmail: values.templatesEmail,
      subjectEmail: values.subjectEmail,
      channelfallback: values.channelfallback,
      accountCahnnels: values.accountCahnnels,
      channelFlows: values.channelFlows,
      events: values.events
    };
  }, [watch()]);

  const accountEmailMessage = useMemo(() => {
    if (!emptyAccountEmail) return '';
    return session.profile === PROFILES.ADMIN
      ? 'La cuenta con la que estás trabajando no tiene una cuenta de Email registrada'
      : 'La cuenta seleccionada en el paso 1 no tiene una cuenta de Email registrada.';
  }, [emptyAccountEmail, session.profile]);

  const templatesEmailMessage = useMemo(() => {
    if (!emptyTemplatesEmail) return '';
    return session.profile === PROFILES.ADMIN
      ? 'La cuenta con la que estás trabajando no tiene plantillas de email registradas.'
      : 'La cuenta de Email seleccionada no tiene plantillas registradas.';
  }, [emptyTemplatesEmail, session.profile]);

  const sectionKeyLabel: Record<string, string> = {
    ALL: 'Variables del Email'
  };

  const [varsEmail, setVarsEmail] = useState<Record<string, Record<string, string>>>({
    ALL: {}
  });

  const fallbackChannelOptions = useMemo(() => {
    if (!formValues.channelFlows || !Array.isArray(formValues.channelFlows)) return [];

    const channelLabels: Record<string, string> = {
      wa: 'WhatsApp',
      sms: 'SMS',
      rcs: 'RCS'
    };

    return formValues.channelFlows
      .filter((channel) => channel !== 'email')
      .map((channel) => ({
        value: channel,
        label: channelLabels[channel] || channel.toUpperCase()
      }));
  }, [formValues.channelFlows]);

  const followUpTemplateOptions = useMemo(() => {
    if (!templatesEmail || !Array.isArray(templatesEmail)) return [];
    return templatesEmail.filter((template) => template.value !== formValues.templatesEmail);
  }, [templatesEmail, formValues.templatesEmail]);

  const eventsConfig = useMemo(() => {
    return getChannelEventsConfig('mail', fallbackChannelOptions, followUpTemplateOptions);
  }, [fallbackChannelOptions, followUpTemplateOptions]);

  const getTemplatesEmail = useCallback(async () => {
    if (!formValues.accountsEmail) return;

    try {
      const response = await TemplatesService.getTemplatesAccountMailing(formValues.account);
      setEmptyTemplatesEmail((!response || response.length === 0) && !!formValues.accountsEmail);
      const allTemplates = Object.values(response).flat() as any[];

      const filtered = allTemplates.filter((template) => template.status === true);

      const finalTemplates = filtered.map(({ _id, name, ...rest }) => ({
        value: _id,
        label: name,
        fullTemplate: { _id, name, ...rest }
      }));

      setTemplatesEmail(finalTemplates);
    } catch (errorCode: any) {
      setValue('templatesEmail', '');
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  }, [formValues.accountsEmail, setValue]);

  const resetConfigVars = useCallback(() => {
    setVarsEmail({
      ALL: {}
    });
    setVariablesBySection({});
  }, []);

  const getVariablesByType = useCallback((template: any) => {
    const variableRegex = /{{([^{}]*)}}/g;
    const result: Record<string, string[]> = {};

    const allVariables = new Set<string>();

    if (template.subject) {
      const subjectMatches = [...template.subject.matchAll(variableRegex)].map(
        (m) => `{{${m[1]}}}`
      );
      subjectMatches.forEach((variable) => allVariables.add(variable));
    }

    const bodyContent = template.html || template.body || '';
    if (bodyContent) {
      const bodyMatches = [...bodyContent.matchAll(variableRegex)].map((m) => `{{${m[1]}}}`);
      bodyMatches.forEach((variable) => allVariables.add(variable));
    }

    if (allVariables.size > 0) {
      result.ALL = Array.from(allVariables);
    }

    return result;
  }, []);

  const renderVariableSelects = useCallback(
    (sectionName: string, variables: string[], sectionKey: string) => {
      return (
        <div key={`variables-${sectionKey}`}>
          <div className="fs-3 fw-bolder text-info">{sectionName}</div>
          <div className="fs-4 mb-7">
            Selecciona una columna para cada una de las variables en {sectionName.toLowerCase()} de
            tu plantilla.
          </div>
          <div className="fs-6 d-flex justify-content-between mb-4">
            <div className="fw-semibold fw-bolder fs-3">Variable</div>
            <div className="d-flex fw-bold fs-3 required">Columna</div>
          </div>
          {variables.map((variable, idx) => (
            <div
              key={`${sectionKey}-${idx}`}
              className="fs-6 d-flex justify-content-between align-items-center my-4 gap-4"
            >
              <div className="fw-semibold fs-3">{variable}</div>
              <div className="fw-bold" style={{ minWidth: '250px' }}>
                <div className="fv-row">
                  <Controller
                    control={control}
                    name={`columnsVariablesEmail.${sectionKey}[${idx}]`}
                    rules={{ required: true }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <>
                        <Select
                          options={formValues.headersSelects}
                          className={
                            error
                              ? 'form-select-custom is-invalid w-250px'
                              : 'form-select-custom w-250px'
                          }
                          classNamePrefix="form-select-custom"
                          placeholder={'Selecciona una columna'}
                          onChange={(selectedOption) => {
                            const selectedValue = selectedOption ? selectedOption.value : null;
                            onChange(selectedValue);

                            if (selectedValue !== null) {
                              setVarsEmail((prev) => {
                                const updated = { ...prev };
                                updated[sectionKey] = {
                                  ...updated[sectionKey],
                                  [variable]: selectedValue
                                };
                                return updated;
                              });
                            }
                          }}
                          value={
                            formValues.headersSelects?.find(
                              (option: any) => option.value === value
                            ) || null
                          }
                          noOptionsMessage={({ inputValue }) =>
                            inputValue
                              ? t('general.no-options', { inputValue })
                              : t('general.no-options')
                          }
                        />
                        <InvalidFeedback error={error} />
                      </>
                    )}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    },
    [formValues.headersSelects, control, t]
  );

  useEffect(() => {
    if (formValues.accountCahnnels?.email) {
      const filtered = formValues.accountCahnnels.email.filter(
        (item: any) => item.status === true && item.config?.status === true
      );

      if (filtered.length === 0) {
        setAccountsEmail([]);
        setEmptyAccountEmail(true);
        return;
      }

      const options = filtered.map(({ config }: any) => ({
        value: config._id,
        label: config.name
      }));
      setEmptyAccountEmail(false);
      setAccountsEmail(options);
    } else {
      setAccountsEmail([]);
      setEmptyAccountEmail(true);
    }
  }, [
    formValues.account,
    formValues.channel,
    formValues.channelfallback,
    formValues.accountCahnnels
  ]);

  useEffect(() => {
    setValue('varsEmail', varsEmail);
  }, [varsEmail, setValue]);

  useEffect(() => {
    const baseValidation =
      !!formValues.accountsEmail && !!formValues.templatesEmail && !!formValues.subjectEmail;
    const isValid = baseValidation && (showEvents ? eventsValid : true);

    if (onValidationChangeRef.current) {
      onValidationChangeRef.current(isValid);
    }
  }, [formValues.accountsEmail, formValues.templatesEmail, formValues.subjectEmail, eventsValid]);

  useEffect(() => {
    if (formValues.channel !== 'mail') {
      setValue('templatesEmail', '');
      setValue('accountsEmail', '');
      setValue('subjectEmail', '');
      setTemplatesEmail([]);
      setVarsEmail({ SUBJECT: {}, BODY: {} });
      setVariablesBySection({});
    }
  }, [formValues.channel, formValues.channelfallback, setValue]);

  useEffect(() => {
    const shouldClearFields =
      formValues.typeCampaign !== 'FALLBACK' ||
      (formValues.typeCampaign === 'FALLBACK' && formValues.channel !== 'mail');

    if (shouldClearFields) {
      setValue('templatesEmail', '');
      setValue('accountsEmail', '');
      setValue('subjectEmail', '');
      setTemplatesEmail([]);
      setVarsEmail({ SUBJECT: {}, BODY: {} });
      setVariablesBySection({});
    }
  }, [formValues.channel, formValues.channelfallback, formValues.typeCampaign, setValue]);

  useEffect(() => {
    if (formValues.account) {
      setValue('templatesEmail', '');
      setValue('accountsEmail', '');
      setValue('subjectEmail', '');
      setTemplatesEmail([]);
      setEmptyTemplatesEmail(false);
    }
    resetConfigVars();
  }, [formValues.account, setValue, resetConfigVars]);

  useEffect(() => {
    setValue('previewTemplateEmail', previewEmail);
  }, [previewEmail, setValue]);

  useEffect(() => {
    if (formValues.accountsEmail) {
      setValue('templatesEmail', '');
      setValue('subjectEmail', '');
      setTemplatesEmail([]);
      getTemplatesEmail();
    }
  }, [formValues.accountsEmail, setValue, getTemplatesEmail]);

  useEffect(() => {
    if (formValues.templatesEmail) {
      resetConfigVars();
      const selectedTemplate = templatesEmail.find(
        (template) => template.value === formValues.templatesEmail
      );
      if (selectedTemplate?.fullTemplate) {
        setPreviewEmail(selectedTemplate.fullTemplate);
        if (selectedTemplate.fullTemplate.subject) {
          setValue('subjectEmail', selectedTemplate.fullTemplate.subject);
        }
      } else {
        setPreviewEmail([]);
        setValue('subjectEmail', '');
      }
    } else {
      setPreviewEmail([]);
      setValue('subjectEmail', '');
    }
  }, [formValues.templatesEmail, templatesEmail, resetConfigVars, setValue]);

  useEffect(() => {
    if (formValues.templatesEmail && previewEmail) {
      resetConfigVars();
      const variables = getVariablesByType(previewEmail);
      setVariablesBySection(variables);
      Object.entries(variables).forEach(([sectionKey, varsArray]) => {
        varsArray.forEach((_, idx: number) => {
          setValue(`columnsVariablesEmail.${sectionKey}[${idx}]`, null);
        });
      });
    }
  }, [formValues.templatesEmail, previewEmail, resetConfigVars, getVariablesByType, setValue]);

  return (
    <>
      <div className="separator separator-dashed separator-content text-dark mb-15">
        <img alt="Logo" src="/assets/media/logos/logo-c.svg" className="w-30px logo-minimize" />
      </div>
      <div className="mb-5 mb-xxl-8">
        <div className="text-left mb-3">
          <Title icon="ki-sms" title="Configuración Email" subtitle="" type="primary" />
        </div>
        <div className="pt-1">
          <div className="d-flex flex-stack cursor-pointer mb-5">
            <div className="d-flex align-items-center">
              <span className="symbol symbol-label"></span>
              <div className="d-flex flex-column">
                <span className="fw-bolder fs-3 required">Cuentas</span>
                <span className="fs-6">Elige la cuenta por la que saldrán los emails.</span>
              </div>
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-12 z-index-3">
              <div className="fv-row  mb-5 z-index-3">
                <Controller
                  control={control}
                  name="accountsEmail"
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        options={accountsEmail}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue
                            ? t('general.no-options', { inputValue })
                            : t('general.no-options')
                        }
                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                        classNamePrefix="form-select-custom"
                        placeholder={'Selecciona una cuenta de Email'}
                        onChange={(
                          selectedOption: {
                            value: string;
                            label: string;
                          } | null
                        ) => {
                          onChange(selectedOption ? selectedOption.value : null);
                        }}
                        value={
                          accountsEmail.find(
                            (option: { value: string; label: string }) => option.value === value
                          ) || null
                        }
                      />
                      <InvalidFeedback error={error} />
                    </>
                  )}
                />
              </div>
            </div>
            {emptyAccountEmail && (
              <div className="alert alert-dismissible bg-light-info border border-info d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
                <Icon icon="ki-information-2" size="fs-2hx" type="info" />
                <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
                  <h5 className="mb-1">¡ Atención !</h5>
                  <span>{accountEmailMessage}</span>
                </div>
              </div>
            )}
          </div>
          <div className="d-flex flex-stack cursor-pointer mb-5">
            <div className="d-flex align-items-center">
              <span className="symbol symbol-label"></span>
              <div className="d-flex flex-column">
                <span className="fw-bolder fs-3 required">Asunto del Email</span>
                <span className="fs-6">Define el asunto que tendrán los emails.</span>
              </div>
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-12">
              <div className="fv-row mb-5">
                <input
                  type="text"
                  className="form-control form-control-lg form-control-solid"
                  placeholder="Escribe el asunto del email"
                  {...register('subjectEmail', { required: 'El asunto del email es requerido' })}
                />
              </div>
            </div>
          </div>
          <div className="d-flex flex-stack cursor-pointer mb-5">
            <div className="d-flex align-items-center">
              <span className="symbol symbol-label"></span>
              <div className="d-flex flex-column">
                <span className="fw-bolder fs-3 required">Plantillas</span>
                <span className="fs-6">Elige la plantilla del email.</span>
              </div>
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-12 z-index-3">
              <div className="fv-row  mb-5 z-index-3">
                <Controller
                  control={control}
                  name="templatesEmail"
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        options={templatesEmail}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue
                            ? t('general.no-options', { inputValue })
                            : t('general.no-options')
                        }
                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                        classNamePrefix="form-select-custom"
                        placeholder={'Selecciona tu plantilla'}
                        onChange={(
                          selectedOption: {
                            value: string;
                            label: string;
                          } | null
                        ) => {
                          onChange(selectedOption ? selectedOption.value : null);
                        }}
                        value={
                          templatesEmail.find(
                            (option: { value: string; label: string }) => option.value === value
                          ) || null
                        }
                      />
                      <InvalidFeedback error={error} />
                    </>
                  )}
                />
              </div>
            </div>
          </div>
          {emptyTemplatesEmail && (
            <div className="alert alert-dismissible bg-light-info border border-info d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
              <Icon icon="ki-information-2" size="fs-2hx" type="info" />
              <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
                <h5 className="mb-1">¡ Atención !</h5>
                <span>{templatesEmailMessage}</span>
              </div>
            </div>
          )}
        </div>

        {/* Sección de variables */}
        {formValues.templatesEmail &&
          variablesBySection &&
          Object.values(variablesBySection).some((vars) => vars.length > 0) && (
            <>
              <div className="separator separator-dashed separator-content text-dark border-2 mb-15">
                <img
                  alt="Logo"
                  src="/assets/media/logos/logo-c.svg"
                  className="w-30px logo-minimize"
                />
              </div>
              <div className="text-left mb-3">
                <Title
                  icon="ki-gear"
                  size="fs-2hx"
                  title="Configuración de variables"
                  type="info"
                />
              </div>

              {Object.entries(variablesBySection).map(([sectionKey, vars], idx, arr) =>
                vars.length > 0 ? (
                  <React.Fragment key={sectionKey}>
                    {renderVariableSelects(sectionKeyLabel[sectionKey], vars, sectionKey)}
                    {idx < arr.length - 1 && (
                      <div className="separator separator-dashed border-2 my-10"></div>
                    )}
                  </React.Fragment>
                ) : null
              )}
            </>
          )}

        {showEvents ? (
          <EventsSection
            channel="mail"
            events={eventsConfig}
            fallbackChannelOptions={fallbackChannelOptions}
            templateOptions={followUpTemplateOptions}
            onValidationChange={setEventsValid}
          />
        ) : null}
      </div>
    </>
  );
}

export default ChannelEmail;
